import Vue from 'vue';
import Vuex from 'vuex';

import { PARTNER_TYPE_PARTNER_ID } from '@/const/constants';
import auth from './modules/auth';
import contractor from './modules/contractor';
import user from './modules/user';
import card from './modules/card';
import partner from './modules/partner';
import * as MUTATIONS from './mutations';
import * as ACTIONS from './actions';
import * as USER_ACTIONS from './modules/user/actions';
import * as USER_MUTATIONS from './modules/user/mutations';
import * as CONTRACTOR_ACTIONS from './modules/contractor/actions';
import * as PARTNER_ACTIONS from './modules/partner/actions';
import * as CONTRACTOR_MUTATIONS from './modules/contractor/mutations';
import * as PARTNER_MUTATIONS from './modules/partner/mutations';
import * as AUTH_ACTIONS from './modules/auth/actions';
import * as AUTH_MUTATIONS from './modules/auth/mutations';
import constant from './modules/constant';

Vue.use(Vuex);

let loadingCounter = 0;

export default new Vuex.Store({
  state: {
    isSplashActive: false,
    isPreloading: false,
    editableSections: [],
    // contractor: [],
    windowWidth: window.innerWidth,
    sidebarWidth: 0,
    PASSWORD_HINT: `Must be at least 8 characters long
    Must have at least 1 uppercase letter [A-Z]
    Must have at least 1 lower case letter [a-z]
    Must have at least 1 number [0-9]
    Special characters allowed ~ ! @ # $ % & ( ) - _ + = { } [ ] | \\ ; : < > , . ? /
    Cannot contain a space`,
  },

  getters: {
    pages: (state) => {
      let pages = [];

      if (state.partner.id) {
        pages = state.partner.pages;
      } else if (state.contractor.id) {
        pages = state.contractor.contractorPages;
      }

      return pages;
    },
    selectedPage: (state) => {
      let selectedPage = [];

      if (state.partner.id) {
        selectedPage = state.partner.selectedPage;
      } else if (state.contractor.id) {
        selectedPage = state.contractor.selectedPage;
      }

      return selectedPage;
    },
    /**
     *
     * @param state
     * @returns Vuex module state ['contractor']
     * */
    contractor: (state) => state.contractor,

    /**
     * Get if auth user is switched to another user
     * @param state
     * @returns {boolean}
     */
    isSwitched: (state) => {
      const authUserId = state.auth2.id;
      const userId = state.user.id;
      if (!authUserId || !userId) return false;
      return authUserId !== userId;
    },

    /**
     * Check if user has privilege
     * @param state
     * @param getters
     * @returns {function(*=): *}
     */
    userAuthenticator: (state, getters) => (privilege) => getters['user/hasPrivilege'](privilege),

    /**
     * Check if auth user has privilege
     * @param state
     * @param getters
     * @returns {function(*=): *}
     */
    authAuthenticator: (state, getters) => (privilege) => getters['auth2/hasPrivilege'](privilege),

    hasAccess: (state, getters) => (privilege, managerPrivilege = null, ignoreSwitch = false) => {
      const { authAuthenticator, userAuthenticator, isSwitched } = getters;

      const preProdPrivilege = { resource: 'pre-prod', code: 'access' };
      try {
        if (!ignoreSwitch && !isSwitched) {
          return userAuthenticator(privilege);
        }
        return ((ignoreSwitch || userAuthenticator(privilege))
                && authAuthenticator(managerPrivilege || privilege))
            || (authAuthenticator(preProdPrivilege)
                && authAuthenticator(managerPrivilege || privilege));
      } catch (exception) {
        return false;
      }
    },
    sidebarWidth: (state) => state.sidebarWidth,
    windowWidth: (state) => state.windowWidth,
    passwordHint: (state) => state.PASSWORD_HINT,
    userTypeId: (state) => {
      let userTypeId = null;

      if (state.partner.id) {
        userTypeId = PARTNER_TYPE_PARTNER_ID;
      } else if (state.contractor.id) {
        userTypeId = state.contractor.typeId;
      }

      return userTypeId;
    },
  },

  mutations: {
    /**
     * Switch splash screen
     * @param state
     * @param isActive
     */
    [MUTATIONS.SET_SPLASH](state, isActive) {
      state.isSplashActive = !!isActive;
    },

    // todo instead use start/finish loading mutations
    [MUTATIONS.SET_PRELOADER](state, isActive) {
      state.isPreloading = !!isActive;
    },
    [MUTATIONS.START_LOADING](state) {
      loadingCounter += 1;
      state.isPreloading = loadingCounter > 0;
    },
    [MUTATIONS.FINISH_LOADING](state) {
      loadingCounter -= 1;
      if (loadingCounter < 0) {
        throw new Error('Loading counter cannot be negative');
      }
      state.isPreloading = loadingCounter > 0;
    },

    /**
     * Reset need confirm param
     */
    [MUTATIONS.RESET_NEED_CONFIRM]() {
      this.state.contractor.needConfirmServiceAgreement = false;
    },

    [MUTATIONS.RESET_NEED_QUIZ]() {
      this.state.contractor.needQuiz = false;
    },
    [MUTATIONS.SET_CONTRACTOR_HAS_PAGE](state, hasPage) {
      this.state.contractor.hasPage = hasPage;
    },
    [MUTATIONS.SET_WINDOW_WIDTH](state) {
      state.windowWidth = window.innerWidth;
    },
    [MUTATIONS.SET_SIDEBAR_WIDTH](state, width) {
      state.sidebarWidth = width;
    },
  },

  actions: {
    /**
     * Switch user by id
     * @param context
     * @param userId
     * @returns {Promise<void>}
     */
    async [ACTIONS.SWITCH_USER](context, userId) {
      const setUserAction = `user/${USER_ACTIONS.USER}`;
      const setContractorAction = `contractor/${CONTRACTOR_ACTIONS.CONTRACTOR}`;
      const setPartnerAction = `partner/${PARTNER_ACTIONS.PARTNER}`;
      const setPartnerPageAction = `partner/${PARTNER_ACTIONS.LOAD_PAGES}`;

      const contractorCLearMutations = `contractor/${CONTRACTOR_MUTATIONS.CLEAR}`;
      const partnerCLearMutations = `partner/${PARTNER_MUTATIONS.CLEAR}`;

      await context.dispatch(setUserAction, userId);
      context.commit(contractorCLearMutations);
      context.commit(partnerCLearMutations);
      await context.dispatch(setContractorAction, context.state.user.contractor);
      await context.dispatch(setPartnerAction, context.state.user.partnerId);
      await context.dispatch(setPartnerPageAction);
    },

    /**
     * Logout from system and clear related state
     * @param context
     * @returns {Promise<void>}
     */
    async [ACTIONS.LOGOUT_AND_CLEAR_STATE](context) {
      const logoutAction = `auth2/${AUTH_ACTIONS.LOGOUT}`;
      const authClearMutations = `auth2/${AUTH_MUTATIONS.CLEAR}`;
      const userClearMutations = `user/${USER_MUTATIONS.CLEAR}`;
      const contractorCLearMutations = `contractor/${CONTRACTOR_MUTATIONS.CLEAR}`;
      const partnerCLearMutations = `partner/${PARTNER_MUTATIONS.CLEAR}`;

      await context.dispatch(logoutAction).then(() => {
        context.commit(authClearMutations);
        context.commit(userClearMutations);
        context.commit(contractorCLearMutations);
        context.commit(partnerCLearMutations);
      });
    },

    async [ACTIONS.LOAD_PAGES](context, payload) {
      let action = null;

      if (context.state.partner.id) {
        action = `partner/${PARTNER_ACTIONS.LOAD_PAGES}`;
      } else if (context.state.contractor.id) {
        action = `contractor/${CONTRACTOR_ACTIONS.LOAD_PAGES}`;
      }

      await context.dispatch(action, payload);
    },

    async [ACTIONS.SET_SELECTED_PAGE](context, payload) {
      let action = null;

      if (context.state.partner.id) {
        action = `partner/${PARTNER_ACTIONS.SET_SELECTED_PAGE}`;
      } else if (context.state.contractor.id) {
        action = `contractor/${CONTRACTOR_ACTIONS.SET_SELECTED_PAGE}`;
      }

      await context.dispatch(action, payload);
    },
  },

  modules: {
    auth2: auth,
    user,
    contractor,
    card,
    partner,
    constant,
  },
});
